import { useCallback, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app";
import { changeJwtLoggedIn, logout } from "../store/userSlice";
import useIsAuthenticated from "./useIsAuthenticated";
import useAuthentication from "./useAuthentication";
import http from "../../common/utilities/HttpModule";
import useAuthenticationTokens from "../../authProvider/hooks/useAuthenticationTokens";
import { ACCESS_TOKEN_COOKIE } from "../../authProvider/constants";

// Injected by index.html (server-side include of the http_authorization header).
const JWT_TOKEN = (window as Window & { __JWT_TOKEN__?: string })
  .__JWT_TOKEN__ as string;
const LOGOUT_URL = `${window.location.origin}/logout.html`;

const useJwtAuthenticate = () => {
  const dispatch = useAppDispatch();
  const hasAuthenticated = useRef(false);

  const { fetchUserAndSetMetadata } = useAuthentication();

  const { isAuthenticated } = useIsAuthenticated();
  const { removeToken } = useAuthenticationTokens();

  const { overviewMap } = useAppSelector((state) => state.appReducer);

  const handleJwtTokenLogin = useCallback(
    async (jwt: string) => {
      if (!jwt?.startsWith("Bearer")) {
        return;
      }

      // Prevent multiple authentications
      if (hasAuthenticated.current) {
        return;
      }

      const jwtToUse = JWT_TOKEN.replace("Bearer ", "").trim();

      if (isAuthenticated) {
        if (overviewMap.length) {
          return;
        }

        hasAuthenticated.current = true;
        await fetchUserAndSetMetadata(jwtToUse, false, false);
        return;
      }

      if (jwtToUse) {
        hasAuthenticated.current = true;
        await fetchUserAndSetMetadata(jwtToUse, false, true);

        dispatch(changeJwtLoggedIn(true));
      }
    },
    [isAuthenticated, dispatch, overviewMap.length, fetchUserAndSetMetadata],
  );

  const handleJwtLogout = useCallback(async () => {
    try {
      await http.get(LOGOUT_URL);
      removeToken(ACCESS_TOKEN_COOKIE);
      dispatch(logout());
      dispatch(changeJwtLoggedIn(false));
      hasAuthenticated.current = false;
    } catch (e) {
      console.error(e);
    }
  }, [dispatch, removeToken]);

  useEffect(() => {
    handleJwtTokenLogin(JWT_TOKEN);
  }, [handleJwtTokenLogin]);

  return handleJwtLogout;
};

export default useJwtAuthenticate;
